import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject
} from '@angular/core';
import { ExternalHeaderComponent } from '../../../../shared/external-header/external-header.component';
import { DatePipe, NgClass, NgForOf, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { FooterComponent } from '../../../../shared/footer/footer.component';
import { LoadMaskComponent } from '../../../../shared/load-mask/load-mask.component';
import { BIPPERZ_BREAKPOINTS, BreakpointsService } from '../../../../shared/services/breakpoint.service';
import { TranslateModule } from '@ngx-translate/core';
import {
  ButtonSize, ButtonState, ButtonType, ChecklistItem, ChecklistProcess,
  EvaluatedItem, EvaluatedProcess, IApplication, IEducation, IInterview, IJobOffer, IMaterial,
  IUser, IWorkExperience, MaterialType
} from '../../../../../assets/types/dtoTypes';
import { MatCard, MatCardActions, MatCardContent, MatCardSubtitle, MatCardTitle } from '@angular/material/card';
import { FlexModule } from '@angular/flex-layout';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import {MatButton, MatIconButton} from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { ButtonComponent } from '../../../../shared/button/button.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ProfilePageService } from '../../user/profile-page/profile-page.service';
import languagesData from '../../../../../assets/language.json';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressBar } from '@angular/material/progress-bar';
import {PATH, PHYSIO_RECOGNITION_DOCS_PROCESS, VISA_PROCESS} from '../../../../../assets/constants';
import { CertificateUploadDialogComponent } from '../../../registration-pages/registration-page/forms/resume-form/certificate-upload-dialog/certificate-upload-dialog.component';
import { VideoService } from '../../../../shared/services/video.service';
import { ShowPopupService } from '../../../../shared/services/show-popup.service';
import { ApplicationActionsComponent } from '../../../../shared/app-application-actions/app-application-actions.component';
import { catchError, filter, finalize, map, of, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {CandidateOverviewPageService} from "./candidate-overview-page.service";
import {MainPageService} from "../main-page/main-page.service";
import { InterviewRescheduleModalComponent } from '../../../../modals/interview-reschedule-modal/interview-reschedule-modal.component';
import { InterviewDeclineModalComponent } from '../../../../modals/interview-decline-modal/interview-decline-modal.component';

@Component({
  selector: 'app-candidate-overview-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ExternalHeaderComponent, NgIf, FooterComponent, LoadMaskComponent, NgClass, TranslateModule,
    MatCardTitle, MatCard, MatCardContent, FlexModule, MatTab, MatCardSubtitle, MatButton,
    MatCardActions, MatTabGroup, NgForOf, MatIcon, MatCheckbox, MatDivider, ButtonComponent,
    DatePipe, MatProgressBar, ApplicationActionsComponent, MatIconButton, NgSwitch, NgSwitchCase
  ],
  templateUrl: './candidate-overview-page.component.html',
  styleUrl: './candidate-overview-page.component.scss',
})
export class CandidateApplicationOverviewPageComponent {
  isMobile = false;
  isLoading = false;
  activeTabIndex = 0;
  isVisumOpen = true;
  offer?: IJobOffer;

  application!: IApplication;
  user!: IUser;

  interviews: IInterview[] = [];
  jobTitle = '';
  companyName = '';
  jobLocation = '';

  lastEducation: IEducation | null = null;
  lastExperience: IWorkExperience | null = null;

  readonly statusSteps = ['PENDING','SHORTLISTED','INVITED_TO_INTERVIEW','INTERVIEWED','OFFERED','ACCEPTED','REJECTED'];
  steps!: Array<{ label: string; done: boolean; date?: Date }>;

  languageCountryMap: { [key: string]: string } = {};
  visaEval: EvaluatedProcess | null = null;

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  private readonly breakpointsService = inject(BreakpointsService);
  private readonly candidateOverviewPageService = inject(CandidateOverviewPageService);
  private readonly profilePageService = inject(ProfilePageService);
  private readonly mainPageService = inject(MainPageService);
  private readonly dialog = inject(MatDialog);
  private readonly videoService = inject(VideoService);
  private readonly popupModalService = inject(ShowPopupService);

  docsEval: EvaluatedProcess | null = null;
  isDocsOpen = true;



  constructor() {
    this.isLoading = true;

    this.breakpointsService
      .observe([BIPPERZ_BREAKPOINTS.s, BIPPERZ_BREAKPOINTS.m_down])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.isMobile = !!(result.s || result.m_down);
        this.cdr.markForCheck();
      });

    languagesData.languages.forEach((lang) => {
      this.languageCountryMap[lang.name] = lang.code;
    });

    this.route.paramMap
      .pipe(
        map(pm => pm.get('id')),
        filter((id): id is string => !!id),
        switchMap(id => this.candidateOverviewPageService.getApplication(id)),
        switchMap((app) => {
          this.application = app;
          this.buildSteps(app);
          this.isLoading = false;

          return of(app).pipe(
            switchMap(() => this.loadOffer(app.jobOfferId)),
            switchMap(() => this.loadInterviews(app.id)),
            switchMap(() => this.loadUser(app.userId)),
          );
        }),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        error: (err) => console.error('Failed to initialize Candidate Application Overview Page', err)
      });
  }

  formatBool(v?: boolean): string {
    return v ? 'Yes' : 'No';
  }

  formatDate(v?: string): string {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(+d) ? v : d.toLocaleDateString();
  }

  formatRange(v?: string): string {
    return v && v.trim().length ? v : '—';
  }

  toggleDocsOpen() {
    this.isDocsOpen = !this.isDocsOpen;
  }

  private loadOffer(jobOfferId: string) {
    if (!this.isUuid(jobOfferId)) {
      this.jobTitle = this.companyName = this.jobLocation = '';
      return of(null);
    }
    return this.mainPageService.getJobOfferById(jobOfferId).pipe(
      tap((offer) => {
        this.offer = offer;
        this.jobTitle = offer?.title ?? '';
        this.companyName = offer?.companyName ?? '';
        this.jobLocation = offer?.location ?? '';
        this.cdr.markForCheck();
      }),
      catchError((err) => {
        console.error('Failed to fetch job offer', err);
        this.jobTitle = this.companyName = this.jobLocation = '';
        this.cdr.markForCheck();
        return of(null);
      })
    );
  }

  private loadInterviews(applicationId: string) {
    return this.candidateOverviewPageService.getInterviewsByApplication(applicationId).pipe(
      map((interviews) =>
        interviews.map((interview) => ({
          ...interview,
          status: this.normalizeInterviewStatus(interview.status)
        }))
      ),
      tap((list) => {
        this.interviews = list;
        this.cdr.markForCheck();
      }),
      catchError((err) => {
        console.error('Failed to fetch interviews', err);
        this.interviews = [];
        this.cdr.markForCheck();
        return of([]);
      })
    );
  }

  private loadUser(userId: string) {
    return this.profilePageService.getProfileInfos(userId).pipe(
      tap((user) => {
        this.user = user;

        this.visaEval = this.evalProcessForUser(VISA_PROCESS, this.user);
        this.docsEval = this.evalProcessForUser(PHYSIO_RECOGNITION_DOCS_PROCESS, this.user);

        this.lastEducation = latestByDateKey(user?.education ?? [], 'completionDate');
        this.lastExperience = latestByDateKey(user?.workExperience ?? [], 'completionDate');

        this.cdr.markForCheck();
      }),
      catchError((err) => {
        console.error('Failed to fetch user profile', err);
        this.user = {} as IUser;
        this.lastEducation = null;
        this.lastExperience = null;
        this.visaEval = null;
        this.cdr.markForCheck();
        return of(null);
      })
    );
  }

  getFlagSrc(language: string): string {
    const code = this.languageCountryMap[language];
    return code ? `assets/icons/flags/square/${code}.svg` : 'assets/icons/flags/square/default.svg';
  }

  toggleVisumOpen() { this.isVisumOpen = !this.isVisumOpen; }

  uploadMaterialFor(item: ChecklistItem) {
    const requiredType = this.getRequiredType(item);
    const dialogRef = this.dialog.open(CertificateUploadDialogComponent, { width: '600px' , data: { userId: this.user.id },
    });
    dialogRef.afterClosed().pipe(
      filter((r: any) => !!r?.title && !!r.url?.trim()),
      switchMap((r: any) =>
        this.videoService.appendSasTokenToVideos(this.user!.id, r.url).pipe(
          switchMap((fileUrl: string) => {
            const newMaterial: Partial<IMaterial> = {
              title: r.title, userId: this.user!.id, url: fileUrl, type: requiredType, description: r.description || 'n/a',
            };
            return this.profilePageService.createNewMaterial(newMaterial);
          })
        )
      ),
      tap((created: IMaterial) => {
        this.user!.materials ??= [];
        this.user!.materials.push(created);
        this.evaluateProcesses();
        this.cdr.markForCheck();
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  async downloadMaterial(mm: { id: string; url: string }) { try { window.open(mm.url, '_blank'); } catch { /* empty */ } }
  async previewMaterial(mm: { id: string; url: string }) { try { window.open(mm.url, '_blank'); } catch { /* empty */ } }
  deleteMaterial(mm: { id: string }) {
    this.popupModalService.showConfirmationPopup({ status: 'warning', translationKey: 'deleteMaterial' })
      .pipe(
        filter(Boolean),
        switchMap(() => this.profilePageService.deleteMaterial(mm.id)),
        tap(() => {
          if (this.user?.materials) this.user.materials = this.user.materials.filter(m => m.id !== mm.id);
          this.evaluateProcesses();
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe();
  }

  private evaluateProcesses() {
    this.visaEval = this.evalProcessForUser(VISA_PROCESS, this.user);
    this.docsEval = this.evalProcessForUser(PHYSIO_RECOGNITION_DOCS_PROCESS, this.user);
  }

  getRequiredType(item: ChecklistItem): MaterialType { return item.requiredTypes; }

  evalProcessForUser(process: ChecklistProcess, user: IUser | null | undefined): EvaluatedProcess {
    const mats = user?.materials ?? [];
    const items: EvaluatedItem[] = process.items.map(item => {
      const matches = this.getMatchesForItem(item, mats);
      return {
        item,
        done: matches.length > 0,
        matchedMaterials: matches.map(m => ({ id: m.id, url: m.url, type: m.type, title: m.title })),
      };
    });
    const requiredItems = items.filter(i => !i.item.optional);
    const completed = requiredItems.filter(i => i.done).length;
    const total = requiredItems.length || 1;
    const progressPercent = Math.round((completed / total) * 100);
    return { process, items, progressPercent, total, completed };
  }

  private buildSteps(app: IApplication) {
    const order = this.statusSteps;
    const currentIdx = order.indexOf(app.status);
    const latestByStatus = new Map<string, Date>();

    if (app.appliedAt) latestByStatus.set('PENDING', new Date(app.appliedAt));
    (app.statusHistory ?? []).forEach(entry => {
      const prev = latestByStatus.get(entry.status);
      const next = new Date(entry.changedAt);
      if (!prev || next.getTime() > prev.getTime()) latestByStatus.set(entry.status, next);
    });

    this.steps = order.map((label, i) => ({
      label,
      done: i <= currentIdx,
      date: latestByStatus.get(label)
    }));
  }

  private getMatchesForItem(item: ChecklistItem, mats: IMaterial[]) {
    if (item.key === 'physio.measlesVacc') {
      return mats.filter(m =>
        m.type === MaterialType.MEASLES_VACCINATION || m.type === MaterialType.MEASLES_TITER_LAB
      );
    }

    return mats.filter(m => m.type === item.requiredTypes);
  }

  startMeeting(meetingId: string) {
    this.router.navigate([PATH.VIDEO, meetingId], {
      queryParams: {
        userId: this.application.userId,
        applicationId: this.application.id,
        jobOffer: this.application.jobOfferId
      },
    });
  }
  sendMessage() { this.router.navigate([PATH.CHAT, this.application.userId]); }
  cancelMeeting(id: string) { console.log(id); }

  openRescheduleModal(interview: IInterview): void {
    const ref = this.dialog.open(InterviewRescheduleModalComponent, {
      data: { interview, candidate: this.user },
      panelClass: 'reschedule-interview-dialog',
    });
    ref.afterClosed().subscribe(result => {
      if (!result) return;
      this.candidateOverviewPageService.requestReschedule(interview.id, result).subscribe({
        next: (updated) => {
          const idx = this.interviews.findIndex(i => i.id === interview.id);
          if (idx !== -1) this.interviews[idx] = { ...this.interviews[idx], ...updated, status: 'RESCHEDULED' };
          this.cdr.markForCheck();
        },
        error: (err) => console.error('Failed to request reschedule', err),
      });
    });
  }

  acceptInterview(interview: IInterview): void {
    this.candidateOverviewPageService.acceptInterview(interview.id).subscribe({
      next: (updated) => {
        const idx = this.interviews.findIndex(i => i.id === interview.id);
        if (idx !== -1) this.interviews[idx] = { ...this.interviews[idx], ...updated, status: 'UPCOMING' };
        this.cdr.markForCheck();
      },
      error: (err) => console.error('Failed to accept interview', err),
    });
  }

  openDeclineModal(interview: IInterview): void {
    const ref = this.dialog.open(InterviewDeclineModalComponent, {
      width: '480px',
      panelClass: 'decline-interview-dialog',
    });
    ref.afterClosed().subscribe(result => {
      if (!result) return;
      this.candidateOverviewPageService.declineInterview(interview.id, result.reason, result.notes).subscribe({
        next: (updated) => {
          const idx = this.interviews.findIndex(i => i.id === interview.id);
          if (idx !== -1) this.interviews[idx] = { ...this.interviews[idx], ...updated, status: 'DECLINED' };
          this.cdr.markForCheck();
        },
        error: (err) => console.error('Failed to decline interview', err),
      });
    });
  }

  private normalizeInterviewStatus(status: string): IInterview['status'] {
    const s = (status || '').toUpperCase();
    if (s === 'INVITED') return 'INVITED';
    if (s === 'SCHEDULED' || s === 'UPCOMING') return 'UPCOMING';
    if (s === 'RESCHEDULED' || s === 'RESCHEDULE_REQUESTED' || s === 'RESCHEDULE' || s === 'PENDING_RESCHEDULE') return 'RESCHEDULED';
    if (s === 'CANCELLED' || s === 'CANCELED') return 'CANCELLED';
    if (s === 'COMPLETED' || s === 'FINISHED' || s === 'DONE') return 'COMPLETED';
    if (s === 'DECLINED') return 'DECLINED';
    return status as IInterview['status'];
  }

  getDuration(startTime: string, endTime: string): string {
    if (!startTime || !endTime) return '0m';
    const [sh = 0, sm = 0] = startTime.split(':').map(Number);
    const [eh = 0, em = 0] = endTime.split(':').map(Number);
    const start = new Date(0, 0, 0, sh, sm).getTime();
    const end = new Date(0, 0, 0, eh, em).getTime();
    const mins = Math.max(0, Math.round((end - start) / 60000));
    return `${mins}m`;
  }

  private isUuid(v?: string): boolean {
    return !!v?.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i);
  }

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonState = ButtonState;
  protected readonly ButtonType = ButtonType;
}

function latestByDateKey<T extends Record<string, any>>(arr: T[], key: keyof T): T | null {
  if (!arr?.length) return null;
  let best = arr[0], bestTime = new Date(best[key]).getTime();
  for (let i = 1; i < arr.length; i++) {
    const t = new Date(arr[i][key]).getTime();
    if (t > bestTime) { best = arr[i]; bestTime = t; }
  }
  return best;
}
